import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import type { AmlRiskPolicy } from '@shared/api/contracts/aml';
import { cn } from '@shared/lib/utils';
import type { ReasonFactorValue } from '@shared/ui';
import {
  Button,
  Input,
  Label,
  ReasonFactorDialog,
  StatusBanner,
  RadioGroup,
  RadioGroupItem,
  Skeleton,
  SkeletonStatus,
  useToast,
} from '@shared/ui';
import { hoursToSeconds, isReasonValid, minutesToSeconds, secondsToHours, secondsToMinutes } from '@entities/compliance';
import { getApiErrorMessage } from '@shared/lib/get-api-error-message';
import { isApiError } from '@shared/api/errors/errors';
import { swrKeys } from '@shared/api/swr/keys';
import { useApiServices } from '@shared/lib/use-api-services';
import { formatDateTime } from '@shared/lib/format-date';

interface PolicyDraft {
  scoreEnforcement: 'SHADOW' | 'ENFORCE';
  withdrawalAutoAllowMaxScore: string;
  withdrawalBlockMinScore: string;
  secondApproverMinScore: string;
  freshnessHours: string;
  timeoutMinutes: string;
  whitelistRecheckIntervalDays: string;
  whitelistRecheckUsageDays: string;
  signalThresholds: Record<string, string>;
}

const draftFromPolicy = (policy: AmlRiskPolicy): PolicyDraft => ({
  scoreEnforcement: policy.scoreEnforcement,
  withdrawalAutoAllowMaxScore: String(policy.withdrawalAutoAllowMaxScore),
  withdrawalBlockMinScore: String(policy.withdrawalBlockMinScore),
  secondApproverMinScore: String(policy.secondApproverMinScore),
  freshnessHours: String(secondsToHours(policy.freshnessSeconds)),
  timeoutMinutes: String(secondsToMinutes(policy.amlPendingTimeoutSeconds)),
  whitelistRecheckIntervalDays: String(policy.whitelistRecheckIntervalDays),
  whitelistRecheckUsageDays: String(policy.whitelistRecheckUsageDays),
  signalThresholds: Object.fromEntries(Object.entries(policy.signalThresholds).map(([key, value]) => [key, String(value)])),
});

const scoreOf = (value: string): number | null => {
  if (!/^\d(\.\d{1,2})?$/.test(value) && value !== '0' && value !== '1') {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return null;
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 1) return null;
  return Math.round(parsed * 100) / 100;
};

export function AmlPolicyForm() {
  const { t } = useTranslation('cabinet');
  const { toast } = useToast();
  const services = useApiServices();
  const policy = useSWR(swrKeys.aml.policy(), () => services.aml.getRiskPolicy());
  const [draft, setDraft] = useState<PolicyDraft | null>(null);
  const [open, setOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<{ id: string; action: 'approve' } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const current = policy.data?.policy;

  useEffect(() => {
    if (current) setDraft(draftFromPolicy(current));
  }, [current]);

  const signals = useMemo(() => Object.keys(draft?.signalThresholds ?? {}).sort(), [draft]);

  const reload = () => void policy.mutate();

  const fail = (err: unknown) => {
    toast(getApiErrorMessage(err, t, 'compliance.errors.generic'), 'error');
    if (isApiError(err) && err.status === 409) reload();
  };

  const save = async (value: ReasonFactorValue) => {
    if (!current || !draft || !isReasonValid(value.reason)) return;
    const autoAllow = scoreOf(draft.withdrawalAutoAllowMaxScore);
    const block = scoreOf(draft.withdrawalBlockMinScore);
    const second = scoreOf(draft.secondApproverMinScore);
    const freshness = hoursToSeconds(Number(draft.freshnessHours));
    const timeout = minutesToSeconds(Number(draft.timeoutMinutes));
    const interval = Number(draft.whitelistRecheckIntervalDays);
    const usage = Number(draft.whitelistRecheckUsageDays);
    if (autoAllow === null || block === null || second === null || autoAllow > block) {
      setError(t('amlPolicy.invalidScores'));
      return;
    }
    const thresholds: Record<string, number> = {};
    for (const signal of signals) {
      const threshold = Number(draft.signalThresholds[signal]);
      if (!Number.isSafeInteger(threshold) || threshold < 0 || threshold > 100) {
        setError(t('amlPolicy.invalidSignal'));
        return;
      }
      thresholds[signal] = threshold;
    }
    const patch: Record<string, unknown> = {};
    if (draft.scoreEnforcement !== current.scoreEnforcement) patch.scoreEnforcement = draft.scoreEnforcement;
    if (autoAllow !== current.withdrawalAutoAllowMaxScore) patch.withdrawalAutoAllowMaxScore = autoAllow;
    if (block !== current.withdrawalBlockMinScore) patch.withdrawalBlockMinScore = block;
    if (second !== current.secondApproverMinScore) patch.secondApproverMinScore = second;
    if (freshness !== current.freshnessSeconds) patch.freshnessSeconds = freshness;
    if (timeout !== current.amlPendingTimeoutSeconds) patch.amlPendingTimeoutSeconds = timeout;
    if (interval !== current.whitelistRecheckIntervalDays) patch.whitelistRecheckIntervalDays = interval;
    if (usage !== current.whitelistRecheckUsageDays) patch.whitelistRecheckUsageDays = usage;
    if (JSON.stringify(thresholds) !== JSON.stringify(current.signalThresholds)) patch.signalThresholds = thresholds;
    if (!Object.keys(patch).length) {
      setError(t('amlPolicy.noChanges'));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await services.aml.patchRiskPolicy({
        version: current.version,
        ...patch,
        secondFactor: value.secondFactor,
      });
      toast(t(result.applied ? 'amlPolicy.applied' : 'amlPolicy.pending'), result.applied ? 'success' : 'success');
      setOpen(false);
      reload();
    } catch (err) {
      fail(err);
    } finally {
      setLoading(false);
    }
  };

  const resolve = async (id: string, action: 'approve' | 'reject' | 'cancel', value?: ReasonFactorValue) => {
    setLoading(true);
    try {
      if (action === 'approve' && value) await services.aml.approveRiskPolicyChange(id, value.secondFactor);
      if (action === 'reject') await services.aml.rejectRiskPolicyChange(id);
      if (action === 'cancel') await services.aml.cancelRiskPolicyChange(id);
      toast(t('amlPolicy.updated'), 'success');
      setPendingAction(null);
      reload();
    } catch (err) {
      fail(err);
    } finally {
      setLoading(false);
    }
  };

  if (!draft || !current) {
    if (policy.error) {
      return <StatusBanner variant="destructive">{t('workspace.loadError')}</StatusBanner>;
    }
    return (
      <SkeletonStatus className="space-y-3" label={t('workspace.loading')}>
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </SkeletonStatus>
    );
  }

  const field = (key: keyof PolicyDraft, label: string, hint?: string) => (
    <div className="space-y-1.5">
      <Label htmlFor={`aml-${key}`}>{label}</Label>
      <Input
        id={`aml-${key}`}
        value={String(draft[key])}
        inputMode="decimal"
        onChange={(event) => setDraft({ ...draft, [key]: event.target.value })}
      />
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );

  return (
    <div className="page-stack">
      {(policy.data?.pendingChanges ?? []).map((change) => (
        <StatusBanner key={change.id}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <span>
              {t('amlPolicy.pendingChange', { id: change.authorUserId, datetime: formatDateTime(change.createdAt) })}
            </span>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button type="button" size="sm" disabled={loading} onClick={() => setPendingAction({ id: change.id, action: 'approve' })}>
                {t('compliance.pending.confirm')}
              </Button>
              <Button type="button" size="sm" variant="outline" disabled={loading} onClick={() => void resolve(change.id, 'reject')}>
                {t('compliance.pending.reject')}
              </Button>
              <Button type="button" size="sm" variant="ghost" disabled={loading} onClick={() => void resolve(change.id, 'cancel')}>
                {t('compliance.pending.cancel')}
              </Button>
            </div>
          </div>
        </StatusBanner>
      ))}
      <form
        className="surface-card space-y-8"
        onSubmit={(event) => {
          event.preventDefault();
          setOpen(true);
        }}
      >
        <section className="space-y-3">
          <div>
            <h2 className="text-base font-medium">{t('amlPolicy.sections.mode')}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{t('amlPolicy.enforcementHint')}</p>
          </div>
          <RadioGroup
            value={draft.scoreEnforcement}
            aria-label={t('amlPolicy.sections.mode')}
            onValueChange={(value) =>
              setDraft({ ...draft, scoreEnforcement: value === 'ENFORCE' ? 'ENFORCE' : 'SHADOW' })
            }
            className="gap-2 sm:grid-cols-2"
          >
            {(['SHADOW', 'ENFORCE'] as const).map((mode) => {
              const key = mode === 'SHADOW' ? 'shadow' : 'enforce';
              const id = `aml-mode-${mode}`;
              return (
                <label
                  key={mode}
                  htmlFor={id}
                  className={cn(
                    'flex cursor-pointer gap-2.5 overflow-visible rounded-lg border border-border p-3 transition-colors',
                    draft.scoreEnforcement === mode && 'border-primary shadow-[inset_0_0_0_1px_hsl(var(--primary))]',
                  )}
                >
                  <RadioGroupItem id={id} value={mode} className="mt-0.5" />
                  <span className="min-w-0 space-y-0.5">
                    <span className="block text-sm font-medium">{t(`amlPolicy.mode.${key}`)}</span>
                    <span className="block text-xs text-muted-foreground">{t(`amlPolicy.mode.${key}Hint`)}</span>
                  </span>
                </label>
              );
            })}
          </RadioGroup>
        </section>

        <section className="space-y-3 border-t border-border pt-6">
          <div>
            <h2 className="text-base font-medium">{t('amlPolicy.sections.thresholds')}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{t('amlPolicy.scoreHint')}</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            {field('withdrawalAutoAllowMaxScore', t('amlPolicy.autoAllow'))}
            {field('withdrawalBlockMinScore', t('amlPolicy.block'))}
            {field('secondApproverMinScore', t('amlPolicy.second'))}
          </div>
        </section>

        <section className="space-y-3 border-t border-border pt-6">
          <div>
            <h2 className="text-base font-medium">{t('amlPolicy.sections.timing')}</h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {field('freshnessHours', t('amlPolicy.freshness'), t('amlPolicy.freshnessHint'))}
            {field('timeoutMinutes', t('amlPolicy.timeout'), t('amlPolicy.timeoutHint'))}
            {field('whitelistRecheckIntervalDays', t('amlPolicy.interval'), t('amlPolicy.daysHint'))}
            {field('whitelistRecheckUsageDays', t('amlPolicy.usage'), t('amlPolicy.daysHint'))}
          </div>
        </section>

        <section className="space-y-3 border-t border-border pt-6">
          <div>
            <h2 className="text-base font-medium">{t('amlPolicy.signals')}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{t('amlPolicy.signalsHint')}</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {signals.map((signal) => (
              <div key={signal} className="space-y-1.5">
                <Label htmlFor={`aml-signal-${signal}`}>{signal}</Label>
                <Input
                  id={`aml-signal-${signal}`}
                  inputMode="numeric"
                  value={draft.signalThresholds[signal]}
                  onChange={(event) =>
                    setDraft({
                      ...draft,
                      signalThresholds: { ...draft.signalThresholds, [signal]: event.target.value },
                    })
                  }
                />
              </div>
            ))}
          </div>
        </section>

        {error ? <StatusBanner variant="destructive">{error}</StatusBanner> : null}
        <div className="flex justify-end border-t border-border pt-6">
          <Button type="submit" className="w-full sm:w-auto">
            {t('amlPolicy.save')}
          </Button>
        </div>
      </form>
      <ReasonFactorDialog
        open={open}
        title={t('amlPolicy.saveTitle')}
        description={t('amlPolicy.saveDescription')}
        confirmLabel={t('amlPolicy.save')}
        loading={loading}
        onOpenChange={setOpen}
        onConfirm={(value) => void save(value)}
      />
      <ReasonFactorDialog
        open={Boolean(pendingAction)}
        title={t('amlPolicy.approveTitle')}
        description={t('amlPolicy.approveDescription')}
        confirmLabel={t('compliance.pending.confirm')}
        reasonRequired={false}
        loading={loading}
        onOpenChange={(next) => {
          if (!next) setPendingAction(null);
        }}
        onConfirm={(value) => {
          if (pendingAction) void resolve(pendingAction.id, 'approve', value);
        }}
      />
    </div>
  );
}
